import { graphHooks } from '@tunasong/graph-lib/react'
import { logger } from '@tunasong/models'
import type { Alert } from '@tunasong/models'
import { features, profilesApi, useThunkDispatch } from '@tunasong/redux'
import type { BandSpace, CalendarEvent, Persisted, Profile, Rider } from '@tunasong/schemas'
import { isCalendarEvent } from '@tunasong/schemas'
import { useNavigateToEntity } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { loadBandPartsInfo } from '../band-parts-info.js'
import { latestRider, riderForEvent, riderFromEvent, riderFromLatest } from '../create-rider.js'
import { generateRiderFromProfiles } from '../generate.js'
import { bandMemberIds } from '../readiness.js'
import type { CreateRiderSource } from './create-rider-dialog.js'
import { CreateRiderDialog } from './create-rider-dialog.js'
import { clearCreateRiderRequest, getCreateRiderRequest, subscribeCreateRiderRequest } from './create-rider-request.js'

export interface CreateRiderHostProps {
  band: Persisted<BandSpace>
  /** The band's riders: the latest is the one to start from, and one made for the same event triggers the guard */
  riders: Persisted<Rider>[]
}

const failed = "Couldn't create the rider. Try again."

/**
 * The Create rider dialog on the band page. It opens on a request (`create-rider-request.ts`) from the band page's own
 * action, the band's Generate command, or a calendar event's "Create rider for this gig"; creates; reports every
 * outcome through the alert; and opens the new rider. The dialog closes only on success.
 */
export const CreateRiderHost: FC<CreateRiderHostProps> = props => {
  const { band, riders } = props
  const dispatch = useThunkDispatch()
  const navigateToEntity = useNavigateToEntity()
  const { createEntity } = graphHooks.useEntityCreate<Rider>()
  /** Open, and for which event; `null` when closed */
  const [openFor, setOpenFor] = useState<{ eventId?: string } | null>(null)
  const [creating, setCreating] = useState(false)

  const request = useSyncExternalStore(subscribeCreateRiderRequest, getCreateRiderRequest, getCreateRiderRequest)
  useEffect(() => {
    if (request?.bandId === band.id) {
      setOpenFor({ eventId: request.eventId })
      clearCreateRiderRequest()
    }
  }, [request, band.id])

  const { entity: eventEntity } = graphHooks.useEntity(openFor?.eventId)
  const event = eventEntity && isCalendarEvent(eventEntity) ? (eventEntity as Persisted<CalendarEvent>) : undefined

  const latest = latestRider(riders)
  const existing = riderForEvent(riders, openFor?.eventId)
  const latestName = latest ? latest.name?.trim() || 'Latest rider' : undefined

  const notify = (alert: Alert) => dispatch(features.notifications.actions.setAlert(alert))
  const close = () => {
    setOpenFor(null)
    setCreating(false)
  }

  /** The new rider's draft and the result sentence, or undefined when members' setups could not be loaded */
  const draftFor = async (source: CreateRiderSource) => {
    const input = { band: { id: band.id, name: band.name }, event }
    if (source === 'latest' && latest) {
      return { draft: riderFromLatest(latest, input), message: `Rider created from ${latestName}.` }
    }
    const result = await dispatch(profilesApi.endpoints.loadProfiles.initiate({ userIds: bandMemberIds(band) }))
    const profiles = result.data as Profile[] | undefined
    if (result.error || !profiles?.length) {
      logger.warn('Create rider: could not load member profiles', { bandId: band.id, error: result.error })
      return undefined
    }
    const bandParts = await loadBandPartsInfo(dispatch, band.id)
    const draft = riderFromEvent(generateRiderFromProfiles(band, profiles, bandParts), input)
    const n = draft.members.length
    return { draft, message: `Rider created from ${n} member ${n === 1 ? 'setup' : 'setups'}.` }
  }

  const handleCreate = async (source: CreateRiderSource) => {
    setCreating(true)
    try {
      const built = await draftFor(source)
      if (!built) {
        notify({ severity: 'error', message: failed })
        setCreating(false)
        return
      }
      const created = await createEntity({ entity: built.draft, parent: band })
      notify({ severity: 'success', message: built.message })
      close()
      navigateToEntity(created)
    } catch (error) {
      logger.error('Create rider failed', { bandId: band.id, error })
      notify({ severity: 'error', message: failed })
      setCreating(false)
    }
  }

  const handleOpenExisting = () => {
    if (existing) {
      close()
      navigateToEntity(existing)
    }
  }

  if (!openFor) {
    return null
  }

  return (
    <CreateRiderDialog
      key={`${openFor.eventId ?? 'band'}:${existing?.id ?? ''}`}
      open
      latestName={latestName}
      existingName={existing ? existing.name?.trim() || 'A rider' : undefined}
      creating={creating || Boolean(openFor.eventId && !event)}
      onCreate={source => void handleCreate(source)}
      onOpenExisting={handleOpenExisting}
      onClose={close}
    />
  )
}

export default CreateRiderHost
